import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    FormControlLabel,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    MenuItem,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import kbaseService from '../services/KbaseService';
import vectorStoreService from '../services/VectorStoreService';

const emptyDraft = {
    kbaseName: '',
    vectorStoreId: 'new',
    ocrEnabled: false,
    chunkSize: 800,
    chunkOverlap: 100,
};

function normalizeString(value) {
    return String(value ?? '').trim();
}

function normalizeOptionalString(value) {
    const s = normalizeString(value);
    return s ? s : undefined;
}

function normalizeEpochToMs(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    // If seconds, convert to ms
    return n < 1e12 ? n * 1000 : n;
}

function Kbases({ embedded = false }) {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const navigate = useNavigate();

    const [kbases, setKbases] = useState([]);
    const [vectorStores, setVectorStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create'); // create | edit
    const [draft, setDraft] = useState(emptyDraft);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const canSubmit = useMemo(() => {
        if (!normalizeString(draft.kbaseName)) return false;
        if (dialogMode === 'edit' && !normalizeString(draft.kbaseId)) return false;
        return true;
    }, [dialogMode, draft.kbaseId, draft.kbaseName]);

    const fetchKbases = async () => {
        if (!clientId) return;
        try {
            setLoading(true);
            setError('');
            const data = await kbaseService.getKbases(clientId);
            setKbases(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Error fetching kbases', e);
            setError(t('kbases.fetchError', 'Failed to load Kbases'));
        } finally {
            setLoading(false);
        }
    };

    const fetchVectorStores = async () => {
        try {
            const data = await vectorStoreService.getVectorStores();
            setVectorStores(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Error fetching vector stores', e);
            // Non-blocking; Kbases can still be managed if backend allows.
        }
    };

    useEffect(() => {
        fetchKbases();
        fetchVectorStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const openCreate = () => {
        setDialogMode('create');
        setDraft({ ...emptyDraft });
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setDialogMode('edit');
        setDraft({
            kbaseId: row?.kbaseId ?? '',
            kbaseName: row?.kbaseName ?? '',
            vectorStoreId: normalizeString(row?.vectorStoreId) || 'new',
            ocrEnabled: Boolean(row?.ocrEnabled),
            chunkSize: Number(row?.chunkSize ?? 0),
            chunkOverlap: Number(row?.chunkOverlap ?? 0),
        });
        setDialogOpen(true);
    };

    const vectorStoreLabelById = useMemo(() => {
        const map = new Map();
        (Array.isArray(vectorStores) ? vectorStores : []).forEach((vs) => {
            const id = normalizeString(vs?.vectorStoreId);
            if (!id) return;
            const name = normalizeString(vs?.name);
            const embeddingVendor = normalizeString(vs?.embeddingVendor);
            const embeddingModel = normalizeString(vs?.embeddingModel);
            const embeddingLabel = [embeddingVendor, embeddingModel].filter(Boolean).join(' / ');

            // Per UX request: show embedding model info (not the raw vectorStoreId).
            if (name && embeddingLabel) {
                map.set(id, `${name} — ${embeddingLabel}`);
            } else if (name) {
                map.set(id, name);
            } else if (embeddingLabel) {
                map.set(id, embeddingLabel);
            } else {
                map.set(id, id);
            }
        });
        return map;
    }, [vectorStores]);

    const sortedVectorStores = useMemo(() => {
        const list = Array.isArray(vectorStores) ? [...vectorStores] : [];
        list.sort((a, b) => {
            const an = normalizeString(a?.name);
            const bn = normalizeString(b?.name);
            if (an && bn) return an.localeCompare(bn);
            return normalizeString(a?.vectorStoreId).localeCompare(normalizeString(b?.vectorStoreId));
        });
        return list;
    }, [vectorStores]);

    const closeDialog = () => setDialogOpen(false);

    const openDelete = (row) => {
        setDeleteTarget(row || null);
        setConfirmDeleteOpen(true);
    };

    const closeDelete = () => {
        setConfirmDeleteOpen(false);
        setDeleteTarget(null);
    };

    const showToast = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity || 'success');
        setSnackbarOpen(true);
    };

    const handleSubmit = async () => {
        if (!clientId) return;
        if (!canSubmit) {
            showToast(t('kbases.validationError', 'KBase Name is required'), 'error');
            return;
        }

        try {
            setLoading(true);
            if (dialogMode === 'create') {
                const payload = {
                    kbaseName: normalizeString(draft.kbaseName),
                    vectorStoreId: normalizeOptionalString(draft.vectorStoreId),
                    ocrEnabled: Boolean(draft.ocrEnabled),
                    chunkSize: Number(draft.chunkSize || 0),
                    chunkOverlap: Number(draft.chunkOverlap || 0),
                };
                Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
                await kbaseService.createKbase(clientId, payload);
                showToast(t('kbases.created', 'KBase created'), 'success');
            } else {
                const payload = {
                    kbaseName: normalizeString(draft.kbaseName),
                    vectorStoreId: normalizeOptionalString(draft.vectorStoreId),
                    ocrEnabled: Boolean(draft.ocrEnabled),
                    chunkSize: Number(draft.chunkSize || 0),
                    chunkOverlap: Number(draft.chunkOverlap || 0),
                };
                Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
                await kbaseService.updateKbase(clientId, draft.kbaseId, payload);
                showToast(t('kbases.updated', 'KBase updated'), 'success');
            }
            setDialogOpen(false);
            await fetchKbases();
        } catch (e) {
            console.error('Error saving kbase', e);
            showToast(e?.message || t('kbases.saveError', 'Failed to save KBase'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!clientId || !deleteTarget?.kbaseId) return;
        try {
            setLoading(true);
            await kbaseService.deleteKbase(clientId, deleteTarget.kbaseId);
            showToast(t('kbases.deleted', 'KBase deleted'), 'success');
            closeDelete();
            await fetchKbases();
        } catch (e) {
            console.error('Error deleting kbase', e);
            showToast(e?.message || t('kbases.deleteError', 'Failed to delete KBase'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseError = () => setError('');

    return (
        <Box sx={{ p: embedded ? 0 : 2 }}>
            {!embedded ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0.5 }}>
                            <Link component={RouterLink} underline="hover" color="inherit" to="/customer-list" sx={{ fontSize: 13 }}>
                                {t('kbases.clientList', 'Client List')}
                            </Link>
                            <Typography color="text.primary" sx={{ fontSize: 13 }}>
                                {t('kbases.breadcrumb', 'Kbases')}
                            </Typography>
                        </Breadcrumbs>
                        <Typography variant="h5" fontWeight="bold">
                            {t('kbases.title', 'KBase List')}{clientId ? `: ${clientId}` : ''}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button variant="outlined" size="small" onClick={() => navigate('/customer-list')} disabled={loading} sx={{ textTransform: 'none', borderRadius: 3 }}>
                            {t('kbases.backToClients', 'Back to Clients')}
                        </Button>
                        <Tooltip title={t('kbases.refresh', 'Refresh')} placement="top" arrow>
                            <span>
                                <IconButton size="small" onClick={fetchKbases} disabled={loading || !clientId}>
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={openCreate}
                            disabled={loading || !clientId}
                            sx={{ textTransform: 'none', borderRadius: 3 }}
                        >
                            {t('kbases.new', 'New KBase')}
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Tooltip title={t('kbases.refresh', 'Refresh')} placement="top" arrow>
                        <span>
                            <IconButton size="small" onClick={fetchKbases} disabled={loading || !clientId}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={openCreate}
                        disabled={loading || !clientId}
                        sx={{ textTransform: 'none', borderRadius: 3 }}
                    >
                        {t('kbases.new', 'New KBase')}
                    </Button>
                </Box>
            )}

            <TableContainer component={Paper} elevation={0} sx={{ mt: 2, borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.kbaseId', 'KBase ID')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.kbaseName', 'KBase Name')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.vectorStoreId', 'Vector Store')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.ocrEnabled', 'OCR')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.chunkSize', 'Chunk Size')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.chunkOverlap', 'Chunk Overlap')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.createdAt', 'Created')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.actions', 'Actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {kbases.map((row) => {
                            const createdMs = normalizeEpochToMs(row.createdAt);
                            const createdLabel = createdMs ? new Date(createdMs).toLocaleDateString() : '-';
                            const vsId = normalizeString(row.vectorStoreId);
                            const vsLabel = vsId ? (vectorStoreLabelById.get(vsId) || vsId) : '-';
                            return (
                                <TableRow key={String(row.kbaseId)} hover>
                                    <TableCell>{row.kbaseId}</TableCell>
                                    <TableCell>{row.kbaseName || '-'}</TableCell>
                                    <TableCell>{vsLabel}</TableCell>
                                    <TableCell>{row.ocrEnabled ? 'Yes' : 'No'}</TableCell>
                                    <TableCell>{Number(row.chunkSize ?? 0)}</TableCell>
                                    <TableCell>{Number(row.chunkOverlap ?? 0)}</TableCell>
                                    <TableCell>{createdLabel}</TableCell>
                                    <TableCell>
                                        <Tooltip title={t('kbases.edit', 'Edit')} placement="top" arrow>
                                            <span>
                                                <IconButton size="small" onClick={() => openEdit(row)} disabled={loading}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={t('kbases.delete', 'Delete')} placement="top" arrow>
                                            <span>
                                                <IconButton size="small" onClick={() => openDelete(row)} disabled={loading}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {kbases.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    {t('kbases.empty', 'No kbases found.')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {dialogMode === 'create' ? t('kbases.createTitle', 'Create KBase') : t('kbases.editTitle', 'Edit KBase')}
                </DialogTitle>
                <DialogContent dividers>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: 2,
                            mt: 1,
                        }}
                    >
                        <TextField
                            label={t('kbases.kbaseName', 'KBase Name')}
                            value={draft.kbaseName}
                            onChange={(e) => setDraft((prev) => ({ ...prev, kbaseName: e.target.value }))}
                            fullWidth
                        />
                        <TextField
                            select
                            label={t('kbases.vectorStoreId', 'Vector Store')}
                            value={draft.vectorStoreId}
                            onChange={(e) => setDraft((prev) => ({ ...prev, vectorStoreId: e.target.value }))}
                            fullWidth
                        >
                            <MenuItem value="new">{t('kbases.vectorStoreCreateNew', 'Create New')}</MenuItem>
                            {sortedVectorStores.map((vs) => {
                                const id = normalizeString(vs?.vectorStoreId);
                                if (!id) return null;
                                const label = vectorStoreLabelById.get(id) || id;
                                return (
                                    <MenuItem key={id} value={id}>
                                        {label}
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                        <FormControlLabel
                            sx={{
                                m: 0,
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                '& .MuiFormControlLabel-label': {
                                    textAlign: 'left',
                                    flexGrow: 1,
                                },
                            }}
                            labelPlacement="start"
                            control={
                                <Switch
                                    checked={Boolean(draft.ocrEnabled)}
                                    onChange={(e) => setDraft((prev) => ({ ...prev, ocrEnabled: e.target.checked }))}
                                    disabled={loading}
                                />
                            }
                            label={t('kbases.ocrEnabled', 'OCR Enabled')}
                        />
                        <TextField
                            label={t('kbases.chunkSize', 'Chunk Size')}
                            type="number"
                            value={draft.chunkSize}
                            onChange={(e) => setDraft((prev) => ({ ...prev, chunkSize: e.target.value }))}
                            fullWidth
                        />
                        <TextField
                            label={t('kbases.chunkOverlap', 'Chunk Overlap')}
                            type="number"
                            value={draft.chunkOverlap}
                            onChange={(e) => setDraft((prev) => ({ ...prev, chunkOverlap: e.target.value }))}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={loading} sx={{ textTransform: 'none' }}>
                        {t('kbases.cancel', 'Cancel')}
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading || !canSubmit} sx={{ textTransform: 'none' }}>
                        {dialogMode === 'create' ? t('kbases.create', 'Create') : t('kbases.save', 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDeleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth>
                <DialogTitle>{t('kbases.deleteTitle', 'Delete KBase')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary">
                        {t('kbases.deleteConfirm', 'Are you sure you want to delete this KBase?')}
                    </Typography>
                    {deleteTarget && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">{deleteTarget.kbaseName || '-'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {deleteTarget.kbaseId ? `ID: ${deleteTarget.kbaseId}` : ''}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDelete} disabled={loading}>
                        {t('kbases.cancel', 'Cancel')}
                    </Button>
                    <Button color="error" variant="contained" onClick={handleDelete} disabled={loading || !deleteTarget?.kbaseId}>
                        {t('kbases.delete', 'Delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error">
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3500}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Kbases;
